import { observer } from 'mobx-react-lite';
import { SpeakerHigh, CircleNotch, WaveTriangle } from '@phosphor-icons/react';
import { useResolve } from '@/infrastructure/useResolve';
import { TOKEN_II18n } from '@/services/i18n/I18n';
import { type MessageKey } from '@/services/i18n/messages';
import { type TTSProviderId } from '@/services/TTSService';
import { TOKEN_PlayerStore } from './PlayerStore';

const pill =
  'flex max-w-[13rem] items-center gap-1.5 rounded-full border border-line bg-surface px-3 py-1.5 text-sm text-ink';

/** Engine picker (device voices vs the downloaded Kokoro model) plus the voice dropdown for the
    active engine. While Kokoro downloads, the voice slot shows progress and Play is gated upstream. */
export const VoiceSelectorView = observer(function VoiceSelectorView() {
  const store = useResolve(TOKEN_PlayerStore);
  const i18n = useResolve(TOKEN_II18n);

  return (
    <div className="flex flex-wrap items-center justify-center gap-2">
      <label className={pill} title={i18n.t('voice.engine')}>
        <SpeakerHigh size={16} className="shrink-0 text-muted" />
        <select
          value={store.ttsProvider}
          onChange={(e) => store.setTtsProvider(e.target.value as TTSProviderId)}
          className="min-w-0 flex-1 cursor-pointer truncate bg-transparent outline-none"
        >
          {store.ttsProviders.map((p) => (
            <option key={p.id} value={p.id}>
              {i18n.t(p.labelKey as MessageKey)}
            </option>
          ))}
        </select>
      </label>

      {store.isVoiceModelLoading ? (
        <span className={pill}>
          <CircleNotch size={16} className="shrink-0 animate-spin text-muted" />
          {i18n.t('voice.loadingModel', { percent: Math.round(store.voiceModelProgress * 100) })}
        </span>
      ) : store.voices.length === 0 ? (
        <span className={pill}>
          <WaveTriangle size={16} className="text-muted" /> {i18n.t('voice.none')}
        </span>
      ) : (
        <label className={pill} title={i18n.t('voice.label')}>
          <WaveTriangle size={16} className="shrink-0 text-muted" />
          <select
            value={store.voiceId ?? ''}
            onChange={(e) => store.setVoice(e.target.value)}
            className="min-w-0 flex-1 cursor-pointer truncate bg-transparent outline-none"
          >
            {store.voices.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name} ({v.lang})
              </option>
            ))}
          </select>
        </label>
      )}
    </div>
  );
});
